import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { emailSchema } from '@pirouette/shared';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';

export const Route = createFileRoute('/forgot-password')({
  component: ForgotPasswordPage,
});

const forgotSchema = z.object({ email: emailSchema });
type ForgotInput = z.infer<typeof forgotSchema>;

function ForgotPasswordPage() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'sent'>('idle');

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<ForgotInput>({
    resolver: zodResolver(forgotSchema),
    mode: 'onBlur',
  });

  async function onSubmit(values: ForgotInput) {
    setStatus('submitting');
    const { error } = await supabase.auth.resetPasswordForEmail(values.email, {
      redirectTo: `${window.location.origin}/auth/reset`,
    });
    if (error) {
      // Anti-enumeration: never reveal whether the address is on file. Log the
      // detail, show the same confirmation either way.
      // eslint-disable-next-line no-console
      console.warn('[forgot-password] resetPasswordForEmail failed', {
        code: error.code,
        status: error.status,
      });
    }
    setStatus('sent');
  }

  return (
    <main id="main" className="flex min-h-dvh items-center justify-center px-6 py-16">
      <div className="w-full max-w-sm">
        <h2 className="font-[var(--font-display)] tracking-[var(--tracking-tight)] text-[var(--text-3xl)]">
          Reset your password
        </h2>

        {status === 'sent' ? (
          <div
            role="status"
            aria-live="polite"
            className="mt-8 rounded-[var(--radius-lg)] border border-[var(--color-success)] bg-[var(--color-success-muted)] p-5 text-[var(--color-ink)]"
          >
            <p className="font-medium">Check your inbox.</p>
            <p className="mt-1 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
              If that email is on file, we've sent a link to reset your password. It expires in 60
              minutes.
            </p>
          </div>
        ) : (
          <>
            <p className="mt-2 text-[var(--color-ink-muted)] text-[var(--text-base)]">
              Enter your email and we'll send you a link to set a new password.
            </p>
            <form
              onSubmit={(e) => void handleSubmit(onSubmit)(e)}
              noValidate
              className="mt-8 space-y-5"
            >
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  aria-invalid={errors.email ? 'true' : 'false'}
                  aria-describedby={errors.email ? 'email-error' : undefined}
                  {...register('email')}
                />
                {errors.email && (
                  <p
                    id="email-error"
                    role="alert"
                    className="mt-2 text-[var(--color-danger)] text-[var(--text-sm)]"
                  >
                    {errors.email.message}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                disabled={!isValid || status === 'submitting'}
                aria-busy={status === 'submitting'}
                className="w-full"
              >
                {status === 'submitting' ? 'Sending link…' : 'Send reset link'}
              </Button>
            </form>
          </>
        )}

        <p className="mt-8 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
          <Link to="/login" className="text-[var(--color-accent)] hover:underline">
            Back to sign in
          </Link>
        </p>
      </div>
    </main>
  );
}
